// Unified error tracking utility for both client and server
import type Bugsnag from '@bugsnag/js';

type BugsnagClient = typeof Bugsnag;

let bugsnagClient: BugsnagClient | null = null;

async function getBugsnagClient(): Promise<BugsnagClient | null> {
  if (bugsnagClient) return bugsnagClient;

  try {
    if (typeof window !== 'undefined') {
      // Client-side
      bugsnagClient = (await import('./bugsnag-client')).default;
    } else {
      // Server-side
      bugsnagClient = (await import('./bugsnag-server')).default;
    }
    return bugsnagClient;
  } catch (error) {
    console.error('Failed to load Bugsnag client:', error);
    return null;
  }
}

/**
 * Capture an exception with optional context metadata
 * Compatible with previous Sentry.captureException API
 */
export async function captureException(
  error: Error | unknown,
  options?: { extra?: Record<string, any> }
) {
  const client = await getBugsnagClient();
  if (!client) {
    console.error('Bugsnag not initialized, error not captured:', error);
    return;
  }

  client.notify(error as Error, (event) => {
    if (options?.extra) {
      event.addMetadata('context', options.extra);
    }
  });
}

/**
 * Capture a message (non-error)
 */
export async function captureMessage(message: string, metadata?: Record<string, any>) {
  const client = await getBugsnagClient();
  if (!client) {
    console.error('Bugsnag not initialized, message not captured:', message);
    return;
  }

  // Bugsnag doesn't have direct message capture, so we'll notify with a custom error
  const error = new Error(message);
  error.name = 'CapturedMessage';

  client.notify(error, (event) => {
    if (metadata) {
      event.addMetadata('message', metadata);
    }
  });
}

// Export for direct use if needed
export { getBugsnagClient };
